import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getValidToken } from '../utils/auth';
import TradingDatePicker from './TradingDatePicker';
import { etTodayStr } from '../utils/tradingCalendar';
import { optionTradingViewUrl } from '../utils/tradingViewLink';

const API_URL = process.env.REACT_APP_API_URL || '';

const STATUS_COLOR = { Active: '#60a5fa', Success: '#22c55e', Failed: '#ef4444' };
const BIAS_COLOR = { bullish: '#22c55e', bearish: '#ef4444', neutral: '#9ca3af' };

function formatTime(unixMs) {
  if (!unixMs) return '—';
  return new Date(unixMs).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York',
  });
}

// Rows that resolved before trail stop was live for their variant (or before
// the ts column existed) never got a real trail-stop price recorded. But the
// rule itself only needs entry/high, both always stored: the trail arms once
// high reaches entry*1.30, locking at entry*1.20. So for old rows we can
// still derive what ts *would* have been under today's rule - clearly marked
// as reconstructed, not an actually-tracked value, since those old signals
// never had the confirmation/trade-corroboration safeguards the live
// version has now. Kept in sync with raiseTrailStop's threshold in
// handlers/smart_entry.go - if that trigger changes again, update this too.
function deriveTS(entry, high) {
  if (!entry) return null;
  if (high < entry * 1.30) return null;
  return entry * 1.20;
}

// % change from the buy price. Resolved rows (Success/Failed) now record the
// actual exit_price at finalize time (TP touch, SL/TS breach, or EOD
// force-close mid) - when present, that's always the right reference, since
// it's the real price the position resolved at, not an inference. Rows
// finalized before this fix shipped have no exit_price, so fall back to the
// old heuristic for them: Failed used the trailing stop (if triggered,
// since the realized low can overshoot it on a bad tick) or else the day's
// low; every other status used the high. Active rows (no exit yet) always
// use the high, the peak reached so far.
function pctFromBuy(entry, high, low, status, ts, exitPrice) {
  if (!entry) return null;
  let ref;
  if (exitPrice > 0) {
    ref = exitPrice;
  } else if (status === 'Failed') {
    ref = ts > 0 ? ts : low;
  } else {
    ref = high;
  }
  return ((ref - entry) / entry) * 100;
}

function PctCell({ entry, high, low, status, ts, exitPrice }) {
  const pct = pctFromBuy(entry, high, low, status, ts, exitPrice);
  if (pct == null) return <td className="py-1.5 pr-3 text-right font-mono text-gray-500">—</td>;
  const color = pct >= 0 ? '#22c55e' : '#ef4444';
  return (
    <td className="py-1.5 pr-3 text-right font-mono font-semibold" style={{ color }}>
      {pct >= 0 ? '+' : ''}{pct.toFixed(1)}%
    </td>
  );
}

function openTradingView(symbol) {
  const url = optionTradingViewUrl(symbol);
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

// Column definitions for the signals table. Each column's `key` points at a
// pre-formatted string field on the enriched row objects built in the
// component below (buildRow) - the filter dropdowns filter on the same
// displayed text the user sees, Excel-style, not the raw numeric value.
const COLUMNS = [
  { key: 'typeLabel', label: 'Type', align: 'left' },
  { key: 'strikeLabel', label: 'Strike', align: 'right' },
  { key: 'dteLabel', label: 'DTE', align: 'right' },
  { key: 'entryLabel', label: 'Entry', align: 'right' },
  { key: 'slLabel', label: 'SL', align: 'right' },
  { key: 'tsLabel', label: 'TS', align: 'right' },
  { key: 'tpLabel', label: 'TP', align: 'right' },
  { key: 'highLabel', label: 'H', align: 'right' },
  { key: 'lowLabel', label: 'L', align: 'right' },
  { key: 'deltaLabel', label: 'Delta', align: 'right' },
  { key: 'addedLabel', label: 'Entry Time', align: 'right' },
  { key: 'resolvedLabel', label: 'Exit Time', align: 'right' },
  { key: 'pctLabel', label: '%', align: 'right' },
  { key: 'status', label: 'Status', align: 'right' },
];

// A row passes the current filter set if every OTHER active column filter
// (skip `excludeKey` - used when computing what values are still available
// to pick for the dropdown currently open, so unchecked values from other
// columns narrow the list, same cascading behavior Excel's AutoFilter has)
// includes this row's value for that column.
function passesFilters(row, filters, excludeKey) {
  for (const key in filters) {
    if (key === excludeKey) continue;
    const sel = filters[key];
    if (sel && !sel.has(row[key])) return false;
  }
  return true;
}

// Excel-style column header filter: a small caret opens a checkbox list of
// every distinct value currently reachable in that column (after the other
// columns' filters are applied), plus a search box and select-all. Filter
// state lives in the parent (one `filters` object shared across columns) so
// clearing/combining filters across columns is trivial.
function FilterHeader({ col, rows, filters, setFilters }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const available = useMemo(() => {
    const vals = new Set();
    for (const r of rows) {
      if (!passesFilters(r, filters, col.key)) continue;
      vals.add(r[col.key]);
    }
    return Array.from(vals).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [rows, filters, col.key]);

  const selected = filters[col.key]; // Set of checked values, or undefined = everything checked
  const isActive = selected != null;
  const isChecked = (v) => selected == null || selected.has(v);
  const searched = search ? available.filter((v) => v.toLowerCase().includes(search.toLowerCase())) : available;
  const allSearchedChecked = searched.length > 0 && searched.every(isChecked);

  function toggleValue(v) {
    setFilters((prev) => {
      const next = { ...prev };
      const cur = next[col.key] ? new Set(next[col.key]) : new Set(available);
      if (cur.has(v)) cur.delete(v); else cur.add(v);
      if (cur.size === available.length) delete next[col.key];
      else next[col.key] = cur;
      return next;
    });
  }

  function toggleAll() {
    setFilters((prev) => {
      const next = { ...prev };
      const cur = next[col.key] ? new Set(next[col.key]) : new Set(available);
      if (allSearchedChecked) {
        for (const v of searched) cur.delete(v);
      } else {
        for (const v of searched) cur.add(v);
      }
      if (cur.size === available.length) delete next[col.key];
      else next[col.key] = cur;
      return next;
    });
  }

  function clearFilter(e) {
    e.stopPropagation();
    setFilters((prev) => {
      const next = { ...prev };
      delete next[col.key];
      return next;
    });
  }

  return (
    <th className={`py-2 pr-3 font-medium relative select-none ${col.align === 'left' ? 'text-left' : 'text-right'}`}>
      <span className="inline-flex items-center gap-1">
        {col.label}
        <button type="button" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
          className="leading-none px-0.5 rounded hover:bg-gray-700"
          style={{ color: isActive ? '#f59e0b' : '#6b7280' }}
          title={isActive ? 'Filter active - click to change' : 'Filter'}>
          ▾
        </button>
      </span>
      {open && (
        <div ref={ref} onClick={(e) => e.stopPropagation()}
          className="absolute z-20 mt-1 bg-gray-800 border rounded-lg shadow-xl p-2 text-left font-normal normal-case"
          style={{ borderColor: '#374151', minWidth: 170, ...(col.align === 'left' ? { left: 0 } : { right: 0 }) }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..."
            className="w-full mb-1.5 px-2 py-1 text-xs bg-gray-900 border rounded text-gray-200"
            style={{ borderColor: '#374151' }} />
          <label className="flex items-center gap-1.5 px-1 py-0.5 text-xs text-gray-300 cursor-pointer border-b mb-1 pb-1.5" style={{ borderColor: '#374151' }}>
            <input type="checkbox" checked={allSearchedChecked} onChange={toggleAll} />
            Select all
          </label>
          <div className="max-h-40 overflow-y-auto">
            {searched.map((v) => (
              <label key={v} className="flex items-center gap-1.5 px-1 py-0.5 text-xs text-gray-300 cursor-pointer hover:bg-gray-700/50 rounded">
                <input type="checkbox" checked={isChecked(v)} onChange={() => toggleValue(v)} />
                <span className="truncate">{v}</span>
              </label>
            ))}
            {searched.length === 0 && <div className="text-xs text-gray-600 px-1 py-1">No values</div>}
          </div>
          {isActive && (
            <button type="button" onClick={clearFilter} className="mt-1.5 w-full text-xs text-blue-400 hover:text-blue-300 text-left px-1">
              Clear filter
            </button>
          )}
        </div>
      )}
    </th>
  );
}

// New discrete-trigger SPX signal engine (backend: handlers/smart_entry.go),
// built to replace the continuous composite-score ranking used by
// BestContractsWidget. Runs side by side with the old widget until it proves
// out over a multi-week comparison — see the "Smart Entry" plan.
//
// Universe = top-20 SPX/SPXW contracts by volume (with rank hysteresis so the
// naturally-rotating leaderboard doesn't thrash). A signal only fires when the
// SPY/SPX underlying trend (RSI/MACD/EMA) confirms direction AND the
// contract's own price shows a pullback-resumption or sustained-higher-lows
// pattern AND a Greeks/liquidity gate passes — one active pick per side.
//
// apiPath/title/subtitle let the same component render both the original
// engine and the delta-consistency-filtered variant (a separate backend
// instance tagged with variant="deltacheck") so the two can be compared
// side by side on live data.
export default function SmartEntryWidget({
  apiPath = '/api/smart-signals',
  title = 'Smart Entry (SPX)',
  subtitle = 'Top-20-by-volume watchlist · underlying trend confirmation · pullback/runner entry timing · Greeks final pick',
}) {
  const [date, setDate] = useState(() => etTodayStr());
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (d) => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${API_URL}${apiPath}?date=${d}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [apiPath]);

  useEffect(() => {
    fetchData(date);
    const timer = setInterval(() => fetchData(date), 5000);
    return () => clearInterval(timer);
  }, [date, fetchData]);

  const bias = data?.bias || 'neutral';
  const signals = useMemo(() => data?.signals || [], [data]);
  const counts = { Active: 0, Success: 0, Failed: 0 };
  for (const s of signals) if (counts[s.status] != null) counts[s.status]++;

  // Enrich each raw signal into a row with every pre-formatted string the
  // table/filters need, computed once per fetch instead of per cell.
  const rows = useMemo(() => signals.map((s, i) => {
    const ts = s.ts;
    const derivedTS = ts > 0 ? null : deriveTS(s.entry, s.high);
    const effectiveTS = ts > 0 ? ts : derivedTS;
    const pct = pctFromBuy(s.entry, s.high, s.low, s.status, effectiveTS, s.exit_price);
    return {
      id: `${s.symbol}-${s.added_at}-${i}`,
      s, dte: s.dte, effectiveTS, derivedTS,
      typeLabel: s.type === 'C' ? 'CALL' : 'PUT',
      strikeLabel: s.strike % 1 === 0 ? s.strike.toFixed(0) : s.strike.toFixed(1),
      dteLabel: String(s.dte),
      entryLabel: `$${s.entry.toFixed(2)}`,
      slLabel: `$${s.sl.toFixed(2)}`,
      tsLabel: effectiveTS > 0 ? `${derivedTS ? '~' : ''}$${effectiveTS.toFixed(2)}` : '—',
      tpLabel: `$${s.tp.toFixed(2)}`,
      highLabel: `$${s.high.toFixed(2)}`,
      lowLabel: `$${s.low.toFixed(2)}`,
      deltaLabel: s.delta.toFixed(2),
      addedLabel: formatTime(s.added_at),
      resolvedLabel: formatTime(s.resolved_at),
      pctLabel: pct == null ? '—' : `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`,
      status: s.status,
    };
  }), [signals]);

  // Excel-style AutoFilter state: colKey -> Set of checked display values.
  // A column absent from this object means "everything checked" (no filter).
  const [filters, setFilters] = useState({});
  const filteredRows = useMemo(
    () => rows.filter((r) => passesFilters(r, filters, null)),
    [rows, filters]
  );
  const hasActiveFilter = Object.keys(filters).length > 0;

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          {title}
        </span>
        <span className="text-xs px-2 py-1 rounded font-semibold" style={{ backgroundColor: (BIAS_COLOR[bias] || '#9ca3af') + '22', color: BIAS_COLOR[bias] || '#9ca3af' }}>
          {bias.charAt(0).toUpperCase() + bias.slice(1)}
        </span>
        {data && (
          <span className="text-xs text-gray-500 font-mono">
            RSI {data.rsi?.toFixed(1)} · MACD {data.macd_hist >= 0 ? '+' : ''}{data.macd_hist?.toFixed(3)} · EMA9 {data.ema9?.toFixed(2)} / EMA20 {data.ema20?.toFixed(2)}
          </span>
        )}
        <div className="flex-1" />
        {Object.entries(counts).map(([st, n]) => (
          <span key={st} className="text-xs font-semibold" style={{ color: STATUS_COLOR[st] }}>{st} {n}</span>
        ))}
        <TradingDatePicker value={date} onChange={setDate} />
      </div>
      <div className="text-xs text-gray-500 mb-3">
        {subtitle}
      </div>
      {error && <div className="text-red-400 text-sm mb-3">Failed to load: {error}</div>}

      {hasActiveFilter && (
        <div className="text-xs text-amber-400 mb-2 flex items-center gap-2">
          <span>Filtered: {filteredRows.length} of {rows.length} rows</span>
          <button type="button" onClick={() => setFilters({})} className="text-blue-400 hover:text-blue-300">
            Clear all filters
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
              {COLUMNS.map((col) => (
                <FilterHeader key={col.key} col={col} rows={rows} filters={filters} setFilters={setFilters} />
              ))}
            </tr>
          </thead>
          <tbody>
            {signals.length === 0 ? (
              <tr><td colSpan={14} className="py-4 text-center text-gray-600">No signals</td></tr>
            ) : filteredRows.length === 0 ? (
              <tr><td colSpan={14} className="py-4 text-center text-gray-600">No rows match the current filter</td></tr>
            ) : filteredRows.map((r) => {
              const s = r.s;
              return (
              <tr key={r.id} onClick={() => openTradingView(s.symbol)}
                className={`border-b cursor-pointer hover:bg-gray-800/50 ${r.dte > 0 ? 'bg-orange-500/15' : ''}`}
                style={{ borderColor: '#1f2937' }}
                title={r.dte > 0 ? `${r.dte} DTE - not a 0DTE contract` : '0DTE contract'}>
                <td className="py-1.5 pr-3">
                  <span className="font-semibold px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: (s.type === 'C' ? '#22c55e' : '#ef4444') + '22', color: s.type === 'C' ? '#22c55e' : '#ef4444' }}>
                    {r.typeLabel}
                  </span>
                </td>
                <td className="py-1.5 pr-3 text-right font-mono">
                  <a href={optionTradingViewUrl(s.symbol)} target="_blank" rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()} title="Open on TradingView"
                    style={{ color: '#60a5fa', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                    {r.strikeLabel}
                  </a>
                </td>
                <td className="py-1.5 pr-3 text-right font-mono font-semibold" style={{ color: r.dte > 0 ? '#f59e0b' : '#9ca3af' }}>{r.dte}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-300">{r.entryLabel}</td>
                <td className="py-1.5 pr-3 text-right font-mono" style={{ color: '#ef4444' }}>{r.slLabel}</td>
                <td className="py-1.5 pr-3 text-right font-mono" style={{ color: r.effectiveTS > 0 ? '#f59e0b' : '#4b5563' }}
                  title={r.derivedTS ? 'Reconstructed: no live ts was recorded for this row, derived from entry/tp/high' : undefined}>
                  {r.tsLabel}
                </td>
                <td className="py-1.5 pr-3 text-right font-mono" style={{ color: '#22c55e' }}>{r.tpLabel}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-400">{r.highLabel}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-400">{r.lowLabel}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-400">{r.deltaLabel}</td>
                <td className="py-1.5 pr-3 text-right text-gray-400">{r.addedLabel}</td>
                <td className="py-1.5 pr-3 text-right text-gray-400">{r.resolvedLabel}</td>
                <PctCell entry={s.entry} high={s.high} low={s.low} status={s.status} ts={r.effectiveTS} exitPrice={s.exit_price} />
                <td className="py-1.5 pr-3 text-right">
                  <span className="font-semibold px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: (STATUS_COLOR[s.status] || '#6b7280') + '22', color: STATUS_COLOR[s.status] || '#6b7280' }}>
                    {s.status}
                  </span>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
